import PDPAccordionSection from '@components/product/PDPAccordionSection';
import DecodeStepsSection from '@components/product/DecodeStepsSection';

/**
 * DecodedProductCard — Wave 4 render surface for the /product-finder
 * page. Wraps the decoded payload from the admin-api public endpoint
 * inside PDPAccordionSection so the card visually matches the PDP.
 *
 * Renders four sections (each collapsible):
 *   1. Summary (family, product line, confidence, is_new flag)
 *   2. Decoded segments (position → raw → decoded lookup)
 *   3. Attributes (key/value grid — this is the "decoded specs" a
 *      user typing an item code wants to see)
 *   4. Cross-check + warnings (schema drift + substantive diffs vs
 *      what's stored in tbl_products, when the code is known)
 *
 * Sandvik brand:
 *   - no ALL CAPS, no font-weight: bold/500/600/700 in Tailwind classes
 *   - sentence-case labels
 *   - rounded-none per tailwind.config comment (borders, not corners)
 */
export default function DecodedProductCard({ decoded }) {
  if (!decoded) return null;

  const {
    item_code,
    product_line,
    family,
    matched_rule,
    regional_non_standard,
    is_new,
    segments = [],
    attributes = {},
    warnings = [],
    candidates,
    cross_check,
  } = decoded;

  const attrEntries = Object.entries(attributes || {});

  return (
    <article
      data-testid="decoded-product-card"
      className="border border-n2 bg-white"
    >
      <header className="px-4 py-4 border-b border-n2 bg-n1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-xl font-normal text-carbon-black font-sandvik-headline">
            {item_code}
          </h2>
          {regional_non_standard && (
            <span
              data-testid="badge-non-standard"
              className="inline-block px-2 py-0.5 text-xs font-normal text-sandvik-gold-dark bg-sandvik-gold-light border border-sandvik-gold-medium"
            >
              non-Standard regional code
            </span>
          )}
          {is_new && !regional_non_standard && (
            <span
              data-testid="badge-is-new"
              className="inline-block px-2 py-0.5 text-xs font-normal text-electric-blue bg-eb-1 border border-eb-2"
            >
              new to catalog
            </span>
          )}
        </div>
        {(family || product_line) && (
          <p className="mt-1 text-sm text-n6">
            {family ? sentenceCase(family) : ''}
            {family && product_line ? ' · ' : ''}
            {product_line ? sentenceCase(product_line) : ''}
          </p>
        )}
      </header>

      {(segments.length > 0 || regional_non_standard) && (
        <DecodeStepsSection
          segments={segments}
          matchedRule={matched_rule}
          regionalNonStandard={regional_non_standard}
        />
      )}

      <PDPAccordionSection title="Decoded specifications" defaultExpanded>
        {attrEntries.length === 0 ? (
          <p className="px-4 py-3 text-sm text-n5">
            No attributes could be inferred from this code.
          </p>
        ) : (
          <dl
            data-testid="decoded-attributes"
            className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 px-4 py-3 text-sm"
          >
            {attrEntries.map(([key, value]) => (
              <div key={key} className="flex justify-between border-b border-n1 py-1.5">
                <dt className="text-n6">{humanise(key)}</dt>
                <dd className="text-carbon-black text-right">{formatValue(value)}</dd>
              </div>
            ))}
          </dl>
        )}
      </PDPAccordionSection>

      {(warnings.length > 0
        || (cross_check?.warnings && cross_check.warnings.length > 0)
        || (cross_check?.schema_drift && cross_check.schema_drift.length > 0)) && (
        <PDPAccordionSection title="Notes and warnings">
          <div className="px-4 py-3 text-sm space-y-2" data-testid="decoded-warnings">
            {warnings.map((w, i) => (
              <p key={`w-${i}`} className="text-vibrant-orange">{w}</p>
            ))}
            {cross_check?.warnings?.map((w, i) => (
              <p key={`ccw-${i}`} className="text-vibrant-orange">
                {w.attribute}: decoded {formatValue(w.decoded)} · stored {formatValue(w.stored)}
              </p>
            ))}
            {cross_check?.schema_drift?.map((d, i) => (
              <p key={`sd-${i}`} className="text-n6">
                Schema drift — {d.attribute} not yet on satellite {d.satellite}.
              </p>
            ))}
          </div>
        </PDPAccordionSection>
      )}

      {candidates && candidates.length > 0 && (
        <PDPAccordionSection title="Other matching families">
          <ul className="px-4 py-3 text-sm space-y-1" data-testid="decoded-candidates">
            {candidates.map((c) => (
              <li key={c.family} className="border-b border-n1 py-1">
                <span>{sentenceCase(c.family)}</span>
              </li>
            ))}
          </ul>
        </PDPAccordionSection>
      )}

      {matched_rule && (
        <footer className="px-4 py-2 text-xs text-n5 border-t border-n2 bg-n1">
          matched rule: {matched_rule}
        </footer>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers — kept local so the component stays self-contained.
// ---------------------------------------------------------------------------

function humanise(key) {
  if (!key) return '';
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\bmm\b/i, 'mm')
    .replace(/\bin\b/i, 'in')
    .replace(/^./, (c) => c.toUpperCase());
}

function sentenceCase(s) {
  if (!s) return '';
  const cleaned = String(s).replace(/[-_]+/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function formatValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

